"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { CalendarCheck, ChevronDown, MapPin, Search, User } from "lucide-react";
import { Logo } from "@/components/ui/Logo";
import { signOut } from "@/lib/auth-actions";
import { ACCOUNT_BOOKINGS_PATH } from "@/lib/booking";
import { cn } from "@/lib/cn";

export type NavLocation = { slug: string; name: string };

/**
 * Top nav. The location switcher is driven by the LIVE locations passed from the
 * [location] layout (draft cities never appear), and `currentSlug` is the city the
 * visitor is on. Switching navigates to the target city's homepage (robust as
 * cities diverge — never a cross-city 404). With a single live city it renders a
 * static label instead of a dropdown.
 */
export function Nav({
  locations,
  currentSlug,
  signedInPhone = null,
}: {
  locations: NavLocation[];
  currentSlug: string;
  /** E.164-ish phone of the signed-in customer, or null when signed out. */
  signedInPhone?: string | null;
}) {
  const [locationOpen, setLocationOpen] = useState(false);
  const locationRef = useRef<HTMLDivElement>(null);

  // Resolve against the KNOWN live locations only. The previous
  // `?? { slug: currentSlug, name: currentSlug }` fallback echoed whatever slug
  // it was handed: harmless under /[location], where the segment is gated, but
  // the account pages have no such gate and pass a user-writable cookie value.
  // An unmatched slug would have put arbitrary text in the header and made the
  // logo href `/${slug}` — protocol-relative, and off-site, for "/evil.com".
  const current = locations.find((l) => l.slug === currentSlug) ?? locations[0] ?? null;
  const multi = locations.length > 1;

  // Close the location menu on outside click / Escape.
  useEffect(() => {
    if (!locationOpen) return;
    function onPointer(e: MouseEvent) {
      if (locationRef.current && !locationRef.current.contains(e.target as Node)) {
        setLocationOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setLocationOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [locationOpen]);

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-surface/85 backdrop-blur-md">
      <nav
        aria-label="Primary"
        className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:gap-4 sm:px-6 lg:px-8"
      >
        {/* Logo -> current city home (root when no city is live) */}
        <Link
          href={current ? `/${current.slug}` : "/"}
          className="flex items-center"
          aria-label="Plug Me home"
        >
          <Logo />
        </Link>

        {/* Location selector — core to the product, so visible on mobile too.
            Rendered only when a live city is actually resolved. */}
        {current && multi ? (
          <div ref={locationRef} className="relative">
            <button
              type="button"
              onClick={() => setLocationOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={locationOpen}
              className="flex items-center gap-1 rounded-full border border-line px-2.5 py-2 text-sm font-medium text-ink transition-colors hover:border-brand/40 hover:bg-brand-tint/50 sm:gap-1.5 sm:px-3"
            >
              <MapPin className="h-4 w-4 shrink-0 text-brand" aria-hidden />
              {current.name}
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-muted transition-transform",
                  locationOpen && "rotate-180",
                )}
                aria-hidden
              />
            </button>

            {locationOpen && (
              <ul
                role="listbox"
                aria-label="Choose your city"
                className="absolute left-0 top-full mt-2 w-48 overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-lg shadow-ink/5"
              >
                {locations.map((city) => (
                  <li key={city.slug} role="option" aria-selected={city.slug === current.slug}>
                    <Link
                      href={`/${city.slug}`}
                      onClick={() => setLocationOpen(false)}
                      className={cn(
                        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-brand-tint",
                        city.slug === current.slug ? "font-semibold text-brand" : "text-body",
                      )}
                    >
                      <MapPin className="h-4 w-4 shrink-0 text-muted" aria-hidden />
                      {city.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : current ? (
          <span className="flex items-center gap-1.5 rounded-full border border-line px-3 py-2 text-sm font-medium text-ink">
            <MapPin className="h-4 w-4 shrink-0 text-brand" aria-hidden />
            {current.name}
          </span>
        ) : null}

        {/* Search */}
        <form
          role="search"
          className="relative ml-auto hidden flex-1 md:block md:max-w-md"
          onSubmit={(e) => e.preventDefault()}
        >
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
            aria-hidden
          />
          <input
            type="search"
            placeholder="Search for a service…"
            aria-label="Search for a service"
            className="w-full rounded-full border border-line bg-canvas py-2.5 pl-10 pr-4 text-sm text-ink placeholder:text-muted transition-colors focus:border-brand focus:bg-surface focus:outline-none"
          />
        </form>

        {/* Right actions */}
        <div className="ml-auto flex items-center gap-1 sm:gap-2 md:ml-0">
          {/* Mobile search icon */}
          <button
            type="button"
            aria-label="Search"
            className="grid h-10 w-10 place-items-center rounded-full text-ink transition-colors hover:bg-brand-tint md:hidden"
          >
            <Search className="h-5 w-5" aria-hidden />
          </button>

          {signedInPhone ? (
            <div className="hidden items-center gap-1 sm:flex">
              <Link
                href={ACCOUNT_BOOKINGS_PATH}
                prefetch={false}
                className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-brand-tint"
              >
                <CalendarCheck className="h-4 w-4" aria-hidden />
                My bookings
              </Link>
              <span
                className="flex items-center gap-1.5 rounded-full bg-brand-tint px-3 py-2 text-sm font-medium text-brand-dark"
                title={`Signed in as ${signedInPhone}`}
              >
                <User className="h-4 w-4" aria-hidden />
                <span className="sr-only">Signed in as </span>
                &middot;&middot;&middot;{signedInPhone.slice(-4)}
              </span>
              <form action={signOut}>
                <button
                  type="submit"
                  className="rounded-full px-3 py-2 text-sm font-medium text-body transition-colors hover:bg-brand-tint hover:text-ink"
                >
                  Log out
                </button>
              </form>
            </div>
          ) : (
            <Link
              href="/login"
              className="hidden items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-brand-tint sm:flex"
            >
              <User className="h-4 w-4" aria-hidden />
              Log in
            </Link>
          )}

          {signedInPhone ? (
            <div className="flex items-center gap-1 sm:hidden">
              <Link
                href={ACCOUNT_BOOKINGS_PATH}
                prefetch={false}
                aria-label="My bookings"
                className="grid h-10 w-10 place-items-center rounded-full text-ink transition-colors hover:bg-brand-tint"
              >
                <CalendarCheck className="h-5 w-5" aria-hidden />
              </Link>
              <form action={signOut}>
                <button
                  type="submit"
                  className="rounded-full border border-line px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-brand-tint"
                >
                  Log out
                </button>
              </form>
            </div>
          ) : (
            <Link
              href="/login"
              className="ml-1 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-dark sm:hidden"
            >
              Log in
            </Link>
          )}
        </div>
      </nav>
    </header>
  );
}
